<template>
  <section :class="[borderColor, backgroundColor, { 'border-b': !isOpen }]">
    <div
      v-if="!hideHeader"
      class="flex items-center justify-between px-2 py-2 text-xs font-medium text-gray-700"
      :class="[borderColor]"
      @click="toggle"
    >
      <div class="flex items-center gap-2">
        <slot
          v-if="$slots.icon"
          name="icon"
        />
        <span
          v-if="title"
          class="py-2 font-medium text-steel-900"
          :class="titleSizeClass"
          v-html="title"
        />
      </div>
      <svg
        class="h-5 w-5 cursor-pointer transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          d="M6.343 7.757 4.929 9.172 12 16.243l7.071-7.071-1.414-1.415L12 13.414 6.343 7.757Z"
        />
      </svg>
    </div>

    <transition
      @before-enter="beforeEnter"
      @enter="enter"
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
      @leave="leave"
      @after-leave="afterLeave"
    >
      <div
        v-show="isOpen"
        class="px-2 text-xs text-body-color"
      >
        <slot />
      </div>
    </transition>
  </section>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  title: { type: String, default: '' },
  modelValue: { type: Boolean, default: true },
  borderColor: { type: String, default: 'border-light-gray' },
  backgroundColor: { type: String, default: 'bg-white' },
  duration: { type: Number, default: 300 },
  hideHeader: { type: Boolean, default: false },
  titleSize: {
    type: String,
    default: 'text-lg',
    validator: (value) =>
      [
        'text-xs',
        'text-sm',
        'text-base',
        'text-lg',
        'text-xl',
        'text-2xl',
        'text-3xl',
      ].includes(value),
  },
});

const emit = defineEmits(['update:modelValue']);
const isOpen = ref(props.modelValue);

const titleSizeClass = computed(() => props.titleSize);

watch(
  () => props.modelValue,
  (v) => (isOpen.value = v)
);
watch(isOpen, (v) => emit('update:modelValue', v));
const toggle = () => (isOpen.value = !isOpen.value);

const setTransition = (el) => {
  el.style.transition = `height ${props.duration}ms ease, opacity ${props.duration}ms ease`;
};

// ----- ENTER -----
const beforeEnter = (el) => {
  el.style.height = '0';
  el.style.opacity = '0';
  el.style.overflow = 'hidden';
};

const enter = (el) => {
  // force first reflow so starting height (0) is taken
  void el.offsetHeight;
  setTransition(el);
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
};

const afterEnter = (el) => {
  el.style.height = 'auto'; // reset
  el.style.transition = '';
  el.style.overflow = 'visible';
};

// ----- LEAVE -----
const beforeLeave = (el) => {
  el.style.height = el.scrollHeight + 'px'; // fixed height to start
  el.style.opacity = '1';
  el.style.overflow = 'hidden';
};

const leave = (el) => {
  // force reflow before collapsing
  void el.offsetHeight;
  setTransition(el);
  el.style.height = '0';
  el.style.opacity = '0';
};

const afterLeave = (el) => {
  el.style.transition = '';
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}

.activeGray {
  border-color: rgba(31, 36, 46, 0.14);
}
</style>
